import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../lib/utils';

/**
 * LazyImage — loads images with a smooth blur-up transition.
 * Shows a blurred placeholder (or colored bg) while loading.
 */
export default function LazyImage({
  src,
  alt = '',
  className = '',
  wrapperClassName = '',
  placeholderColor = 'bg-muted',
  aspectRatio,
  ...props
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // Reset state when src changes
    setLoaded(false);
    setError(false);

    // Check if image is already cached
    if (imgRef.current?.complete && imgRef.current?.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  if (!src || error) {
    return (
      <div className={cn(
        'flex items-center justify-center',
        placeholderColor,
        wrapperClassName
      )} style={aspectRatio ? { aspectRatio } : undefined}>
        <svg className="w-8 h-8 text-muted-foreground/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="8.5" cy="8.5" r="1.5" />
          <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </svg>
      </div>
    );
  }

  return (
    <div
      className={cn('relative overflow-hidden', placeholderColor, wrapperClassName)}
      style={aspectRatio ? { aspectRatio } : undefined}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
        className={cn(
          'transition-all duration-500 ease-out',
          loaded ? 'opacity-100 blur-0 scale-100' : 'opacity-0 blur-sm scale-105',
          className
        )}
        {...props}
      />
    </div>
  );
}
